import React from 'react';
import { useForm } from 'react-hook-form';
import { Post } from '../../../types/blog';
import { EditorContent } from '../../../types/editor';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createPost, updatePost } from '../api/postApi';
import { useAuthContext } from '../../../components/AuthProvider';
import { useEditPost } from '../hooks/useEditPost';
import Editor from '../components/editor/Editor';
import TagInput from '../components/TagInput';
import { generateUniqueSlug } from '../utils/slugify';

interface BlogEditorProps {
  post?: Post;
  onSave: (post: Partial<Post>) => Promise<void>;
}

interface FormData {
  title: string;
  content: string;
  status: 'draft' | 'published';
  tags: string[];
}

export default function BlogEditor({ post, onSave }: BlogEditorProps) {
  const { register, handleSubmit, setValue, watch } = useForm<FormData>({
    defaultValues: {
      title: post?.title || '',
      content: post?.content ? JSON.stringify(post.content) : '',
      status: post?.status || 'draft',
      tags: post?.tags || []
    }
  });

  const { id } = useParams();
  const isEditMode = !!id;
  const { data: existingPost, isLoading: isLoadingPost } = useEditPost(id);
  
  const [tags, setTags] = useState<string[]>([]);
  
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthContext();

  useEffect(() => {
    if (existingPost) {
      setValue('title', existingPost.title);
      setValue('content', existingPost.content ? JSON.stringify(existingPost.content) : '');
      setValue('tags', existingPost.tags || []);
    }
  }, [existingPost, setValue]);

  const mutation = useMutation({
    mutationFn: async (data: FormData) => {
      if (!user?.id) throw new Error('未登录');
      
      const content = data.content ? JSON.parse(data.content) : { text: '', html: '', data: null };
      const excerpt = content.text.slice(0, 200) + (content.text.length > 200 ? '...' : '');
      
      if (isEditMode && id) {
        return updatePost(id, {
          title: data.title,
          content,
          excerpt,
          tags: data.tags
        });
      } else {
        const slug = generateUniqueSlug(data.title);
        return createPost({
          title: data.title,
          content,
          excerpt,
          slug,
          status: 'draft',
          author_id: user.id,
          tags: data.tags
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      navigate('/admin/content');
    },
  });

  const onSubmit = async (data: FormData) => {
    await mutation.mutate(data);
  };

  if (isEditMode && isLoadingPost) {
    return <div className="text-center py-8">加载中...</div>;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700">
          标题
        </label>
        <input
          type="text"
          {...register('title', { required: true })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          内容
        </label>
        <div className="mt-1">
          <Editor 
            initialContent={watch('content') ? JSON.parse(watch('content')) : ''}
            onChange={(value) => setValue('content', JSON.stringify(value))}
            placeholder="在这里写入文章内容..."
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          标签
        </label>
        <TagInput
          tags={tags}
          onChange={setTags}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          状态
        </label>
        <select
          {...register('status')}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
        >
          <option value="draft">草稿</option>
          <option value="published">发布</option>
        </select>
      </div>

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50"
        >
          取消
        </button>
        <button
          type="submit"
          disabled={mutation.isPending}
          className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
        >
          {mutation.isPending ? '保存中...' : '保存'}
        </button>
      </div>

      {mutation.isError && (
        <div className="mt-2 text-sm text-red-600">
          保存失败，请重试
        </div>
      )}
    </form>
  );
}